import axios, { AxiosResponse } from "axios";
import { configService, ArrayFromEnvelope, ObjectFromEnvelope } from "./config.service";
import { AbsenceRequest, IAbsenceRequest } from "../models/absenceRequest-model";
import { convertDateOnlyToUtc } from "@/utils/dateUtils";
import { subDays } from "date-fns";

class AbsenceRequestService {
  async getAbsenceRequests(fromDate: Date): Promise<AbsenceRequest[]> {
    const utcFrom = convertDateOnlyToUtc(subDays(fromDate, 1));
    return new Promise((resolve, reject) => {
      axios
        .get<IAbsenceRequest[]>(
          configService.personaleOrAdminUrl("request_absence/absence_requests.json"),
          {
            params: {
              from_date: utcFrom
            }
          }
        )
        .then((response: AxiosResponse) => {
          const absenceRequests: AbsenceRequest[] = AbsenceRequest.fromJSONArray(
            ArrayFromEnvelope<AbsenceRequest>(response.data)
          );
          resolve(absenceRequests);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getAbsenceRequest(externalId: string): Promise<AbsenceRequest> {
    return new Promise((resolve, reject) => {
      axios
        .get<IAbsenceRequest>(
          configService.personaleOrAdminUrl(`absence-requests/${externalId}.json`)
        )
        .then((response: AxiosResponse) => {
          resolve(AbsenceRequest.fromJSON(ObjectFromEnvelope<IAbsenceRequest>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async createAbsenceRequest(request: IAbsenceRequest): Promise<AbsenceRequest> {
    return new Promise((resolve, reject) => {
      axios
        .post<IAbsenceRequest>(configService.APIInstitutionUrl("absence-requests.json"), request)
        .then((response: AxiosResponse) => {
          resolve(AbsenceRequest.fromJSON(ObjectFromEnvelope<IAbsenceRequest>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async updateAbsenceRequest(externalId: string, request: AbsenceRequest): Promise<AbsenceRequest> {
    return new Promise((resolve, reject) => {
      axios
        .put<AbsenceRequest>(
          configService.APIInstitutionUrl(`request_absence/update/${externalId}.json`),
          request.convertToPutObject()
        )
        .then((response: AxiosResponse) => {
          resolve(AbsenceRequest.fromJSON(ObjectFromEnvelope<IAbsenceRequest>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async deleteAbsenceRequest(externalId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      axios
        .delete(configService.APIInstitutionUrl(`absence-requests/${externalId}.json`))
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async approveAbsenceRequest(
    externalId: string,
    request: IAbsenceRequest
  ): Promise<AbsenceRequest> {
    return new Promise((resolve, reject) => {
      axios
        .post<IAbsenceRequest>(
          configService.personaleOrAdminUrl(`request_absence/approve/${externalId}.json`),
          request
        )
        .then((response: AxiosResponse) => {
          resolve(AbsenceRequest.fromJSON(ObjectFromEnvelope<IAbsenceRequest>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async rejectAbsenceRequest(externalId: string): Promise<AbsenceRequest> {
    return new Promise((resolve, reject) => {
      axios
        .post<IAbsenceRequest>(
          configService.personaleOrAdminUrl(`request_absence/reject/${externalId}.json`)
        )
        .then((response: AxiosResponse) => {
          resolve(AbsenceRequest.fromJSON(ObjectFromEnvelope<IAbsenceRequest>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}

export const absenceRequestService = new AbsenceRequestService();
